/**
 * Attack profiles — where a given kind of blow tends to land (AoD §3.1).
 *
 * The zone weighting is per *attack*, not per weapon, so a spear that is
 * thrust and a spear that is swung as a staff spread their hits differently
 * without either being a separate weapon. Creatures tune data here rather than
 * code: a boar gores at knee height and a wolf goes for the limbs it can reach,
 * and both are eight numbers in a table.
 *
 * Weights are relative and need not sum to anything. Zones the target does not
 * have are dropped before the roll, so `gore` works on a man and on a stag
 * without a variant for either.
 */
/** A swung edge: it comes over the top and takes head, arms and legs. */
export const SWING = {
    id: 'swing',
    name: 'swing',
    damageType: 'cut',
    vitalZoneBiasPerSkill: 0.15,
    zoneWeights: {
        head: 12,
        eye: 2,
        neck: 4,
        chest: 18,
        abdomen: 12,
        arm: 20,
        hand: 8,
        leg: 18,
        foot: 6,
    },
};
/** A thrust point: it goes for the middle, where the man is widest. */
export const THRUST = {
    id: 'thrust',
    name: 'thrust',
    damageType: 'puncture',
    vitalZoneBiasPerSkill: 0.2,
    zoneWeights: {
        head: 8,
        eye: 2,
        neck: 6,
        chest: 30,
        abdomen: 24,
        arm: 12,
        hand: 6,
        leg: 10,
        foot: 2,
    },
};
/** A loosed arrow: mostly torso, with enough limb hits to stay uncertain. */
export const SHOT = {
    id: 'shot',
    name: 'shot',
    damageType: 'puncture',
    // A novice looses at the animal; a practised archer settles on the largest
    // vital line. This biases the same weighted roll rather than inventing a
    // hidden called shot, so even an expert can still take a limb.
    vitalZoneBiasPerSkill: 0.35,
    zoneWeights: {
        head: 8,
        eye: 1,
        neck: 4,
        chest: 34,
        abdomen: 24,
        arm: 12,
        hand: 4,
        leg: 10,
        foot: 3,
    },
};
/** A club or a staff. Much like a swing, and fonder of the skull. */
export const BASH = {
    id: 'bash',
    name: 'bash',
    damageType: 'blunt',
    zoneWeights: {
        head: 14,
        eye: 1,
        neck: 3,
        chest: 22,
        abdomen: 14,
        arm: 18,
        hand: 8,
        leg: 16,
        foot: 4,
    },
};
/**
 * Jaws. A wolf takes the limb that is nearest and the throat if it is offered;
 * it does not swing at a head it would have to leap for.
 */
export const BITE = {
    id: 'bite',
    name: 'bite',
    damageType: 'bite',
    zoneWeights: {
        head: 10,
        eye: 1,
        neck: 8,
        chest: 10,
        abdomen: 12,
        arm: 22,
        hand: 12,
        leg: 20,
        foot: 5,
    },
};
/**
 * A boar's tusk, coming up from below. The reason a gored man's wounds are all
 * in his legs and his belly, and the case that proves attack-weighted location
 * is worth having at all.
 */
export const GORE = {
    id: 'gore',
    name: 'gore',
    damageType: 'puncture',
    zoneWeights: {
        head: 1,
        neck: 1,
        chest: 8,
        abdomen: 22,
        arm: 6,
        hand: 2,
        leg: 40,
        foot: 20,
    },
    /** Half a ton behind it. */
    damageBonus: 1,
};
/**
 * A stag's forehooves, coming down from a rear. The reason a cornered deer is
 * not a safe animal to close with: it is above you, and it is using its whole
 * weight.
 */
export const STRIKE = {
    id: 'strike',
    name: 'strike',
    damageType: 'blunt',
    zoneWeights: {
        head: 20,
        eye: 2,
        neck: 8,
        chest: 24,
        abdomen: 10,
        arm: 16,
        hand: 8,
        leg: 8,
        foot: 2,
    },
};
/**
 * Antlers, swept sideways. Wide rather than deep — a stag tosses a man rather
 * than skewering him, so this is where knockdown lives and lethality does not.
 */
export const TOSS = {
    id: 'toss',
    name: 'toss',
    damageType: 'puncture',
    zoneWeights: {
        head: 4,
        eye: 1,
        neck: 4,
        chest: 22,
        abdomen: 24,
        arm: 18,
        hand: 6,
        leg: 16,
        foot: 4,
    },
};
/** Hooves, on somebody already down. Uniform-ish and merciless. */
export const TRAMPLE = {
    id: 'trample',
    name: 'trample',
    damageType: 'blunt',
    zoneWeights: {
        head: 4,
        neck: 1,
        chest: 14,
        abdomen: 16,
        arm: 12,
        hand: 8,
        leg: 22,
        foot: 24,
    },
};
/**
 * A fall from height. The environmental source that proves the pipeline needs
 * no special case: a severity roll replaces the contested margin, and
 * everything downstream — location, armour coverage, wound table, narration —
 * is the same code that resolves a sword.
 */
export const FALL = {
    id: 'fall',
    name: 'fall',
    damageType: 'blunt',
    zoneWeights: {
        head: 10,
        neck: 4,
        chest: 16,
        abdomen: 10,
        arm: 16,
        hand: 10,
        leg: 20,
        foot: 14,
    },
};
/** Fire. Mostly it is the hands, because it is mostly the hands that reach in. */
export const FIRE = {
    id: 'fire',
    name: 'fire',
    damageType: 'burn',
    zoneWeights: { head: 10, chest: 15, abdomen: 10, arm: 25, hand: 40 },
};
const PROFILES = [
    SWING,
    THRUST,
    SHOT,
    BASH,
    BITE,
    GORE,
    STRIKE,
    TOSS,
    TRAMPLE,
    FALL,
    FIRE,
];
export const ATTACK_PROFILES = new Map(PROFILES.map((profile) => [profile.id, profile]));
export function attackProfile(id) {
    const profile = ATTACK_PROFILES.get(id);
    if (!profile)
        throw new Error(`unknown attack profile '${id}'`);
    return profile;
}
//# sourceMappingURL=attackProfiles.js.map